import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { AppConfigService, WINDOW, WindowService } from '@core/index';
import { LanguageService } from '@core/language/language.service';
import { convertToDate } from '@core/utils/date.utils';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { AuthFacade } from '@store/auth/auth.facade';
import { ItineraryFacade } from '@store/itinerary/itinerary.facade';
import { Flight } from '@store/itinerary/types';
import isPast from 'date-fns/isPast';
import { JbPageEvent, JbPaginatorDirectionEnum } from 'jb-component-library';
import { clamp } from 'ramda';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ExtraLegAttributes } from './types';
import { TravelModesResponse } from './types/travel-modes.type';

@Component({
  selector: 'jb-travel-modes-container',
  templateUrl: './travel-modes.component.html',
  styleUrls: ['./travel-modes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TravelModesContainerComponent implements OnInit {
  @Input() copy: TravelModesResponse;
  profile: any;
  manageBookingLink: BehaviorSubject<string> = new BehaviorSubject('');
  upcomingTripData: Flight[] = [];
  isError: object;
  legs: number;
  segmentDestination: string;
  legAttributes = [];
  isMobile: boolean;
  pnr: string;
  showServiceUnavailableMessage = false;
  mybGoToDX = false;
  mybEnableHealthCheckService = false;
  activeIndex = 0;
  // The hard coded texts below will be replaced by CMS values
  errorHeadline = "It's not you. It's us.";
  errorCopy =
    'Your upcoming trips should be back any minute. Please try again soon. We apologize for the inconvenience.';
  errorCTA = 'Manage your trip';

  private subscriptions: Subscription = new Subscription();

  constructor(
    private appConfigService: AppConfigService,
    private itineraryFacade: ItineraryFacade,
    private windowService: WindowService,
    private authFacade: AuthFacade,
    @Inject(WINDOW) private window,
    private cdRef: ChangeDetectorRef,
    private languageService: LanguageService,
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.authFacade.authProfile
        .pipe(filter(Boolean), take(1))
        .subscribe(() => {
          this.window.requestIdleCallback(() =>
            this.itineraryFacade.checkForItinerary(),
          );
        }),
    );
    this.subscriptions.add(
      combineLatest([
        this.itineraryFacade.flight,
        this.itineraryFacade.itinerary,
        this.windowService.windowSizeChanged,
        this.authFacade.authProfile,
        this.itineraryFacade.upcomingFlightError$,
      ]).subscribe(([flights, itinerary, isMobile, profile, error]) => {
        this.profile = profile;
        this.isMobile = !!isMobile.small;
        if (itinerary.flight[0]) {
          if (error || !itinerary.flight[0].airLegList[0].flightInfo.length) {
            this.showServiceUnavailableMessage = true;
          }
        }
        if (!!profile && flights && flights.length) {
          this.showServiceUnavailableMessage = false;
          this.upcomingTripData = flights;
          this.setManageTripsUrl();

          // Don't show destination if more then 1 segment is returned
          if (this.upcomingTripData.length > 1) {
            this.segmentDestination = '';
          } else {
            this.legs = this.upcomingTripData[0].airLegList.length;
            this.segmentDestination =
              this.upcomingTripData[0].airLegList[
                this.legs - 1
              ].destinationName;
          }

          // Go through each segment
          this.upcomingTripData.forEach(segment => {
            segment.airLegList.forEach(leg => {
              this.legAttributes.push(this.getLegAttributes(leg));
            });
          });
          this.cdRef.markForCheck();
        }
      }),
    );
  }

  // Set extra attributes for each leg. Doing it here to avoid extra calc on the html side
  getLegAttributes(leg): ExtraLegAttributes {
    const attributes: ExtraLegAttributes = {
      showTerminalGate: true,
      displayTimes: true,
      hasArrived: false,
      hasDeparted: false,
      // removes airport code from city name
      originCity: leg.originName.replace(/\([A-Za-z]{3}\)/, '').trim(),
      destinationCity: leg.destinationName
        .replace(/\([A-Za-z]{3}\)/, '')
        .trim(),
    };

    const flightInfo = leg.flightInfo[0];

    const legStatus = flightInfo.flightStatus;
    if (legStatus) {
      attributes.statusColor = this.getStatus(legStatus);

      // Hide right column and bottom row when status is cancelled
      if (legStatus.indexOf('cancelled') >= 0) {
        attributes.showTerminalGate = false;
        attributes.displayTimes = false;
      }
    }

    // Flight has departed
    attributes.hasDeparted =
      !!flightInfo.estimatedDepartureTime &&
      isPast(
        convertToDate(flightInfo.estimatedDepartureTime + leg.originOffSet),
      );

    // Flight has arrived
    attributes.hasArrived =
      !!flightInfo.estimatedArrivalTime &&
      isPast(
        convertToDate(flightInfo.estimatedArrivalTime + leg.destinationOffSet),
      );

    return attributes;
  }
  handlePaginatorEvent(event: JbPageEvent) {
    if (event.direction === JbPaginatorDirectionEnum.previous) {
      this.activeIndex = clamp(
        0,
        this.upcomingTripData[0].airLegList.length,
        this.activeIndex - 1,
      );
    }

    if (event.direction === JbPaginatorDirectionEnum.next) {
      this.activeIndex = clamp(
        0,
        this.upcomingTripData[0].airLegList.length,
        this.activeIndex + 1,
      );
    }
  }

  // Set custom status css class based on status of each leg
  getStatus(legStatus: string): string {
    let actualStatus = 'other-status';

    legStatus = legStatus.toLowerCase();
    if (legStatus.indexOf('delay') >= 0) {
      actualStatus = 'delayed-status';
    }
    if (legStatus.indexOf('cancelled') >= 0) {
      actualStatus = 'cancelled-status';
    }
    if (legStatus.indexOf('boarding') >= 0) {
      actualStatus = 'boarding-status';
    }
    // Doors closing or closed. This would overwrite the borading color ^
    if (legStatus.indexOf('clos') >= 0) {
      actualStatus = 'closing-status';
    }

    return actualStatus;
  }

  setManageTripsUrl(): void {
    if (this.mybGoToDX) {
      const lang = this.languageService.getLanguage();
      const useLanguageInUrl: boolean =
        this.appConfigService.useLanguageInUrl(lang);

      const url = `${
        this.window.location.origin.includes('localhost')
          ? 'https://www-dev2.jetblue.com'
          : window.location.origin
      }${useLanguageInUrl ? `/${lang}` : ''}/manage-trips`;
      this.manageBookingLink.next(url);
    } else {
      this.manageBookingLink.next(
        `${this.appConfigService.baseBook}/B6/ReservationRetrieveRemoteExternal.do?bookingReference=${this.upcomingTripData[0].pnr}&remoteSearchCriteria.travelerLastName=${this.profile.lname}`,
      );
    }
  }
}

export const travelModesContainerResolver = (_, data) => {
  return {
    copy: data.myTrips || [],
    mybGoToDX: booleanStringCheck(data?.configs?.[0]?.mybGoToDX || false),
    mybEnableHealthCheckService: booleanStringCheck(
      data?.configs?.[0]?.mybEnableHealthCheckService || false,
    ),
  };
};
